<template>
  <div class="course-editor">
    <weui-group tips="如每周三、周五都安排同一时间上课">
      <weui-cell :text="formatDate(draft.date)" :link="true" @click="selectDate" />
      <weui-switch v-model="undetermined">
        <div>未能确定当日具体时间</div>
        <div class="info-line">将在提前一天晚21时提醒设置具体时间</div>
      </weui-switch>
      <template v-if="!undetermined">
        <weui-cell label="开始时间" :tips="formatTime(draft.start_time) || '--:--'" :link="true" @click="selectStartTime" />
        <weui-cell label="结束时间" :tips="formatTime(draft.end_time) || '--:--'" :link="true" @click="selectEndTime" />
      </template>
      <weui-cell label="每周重复" :tips="formatWeekdays(draft.weekdays) || '从不'" :link="true" @click="selectWeekdays" />
    </weui-group>
    <weui-group>
      <weui-cell v-if="is_coach_course">自己训练</weui-cell>
      <weui-cell v-else-if="is_private_course">私人事件</weui-cell>
      <weui-cell v-else :text="trainer && trainer.display_name" :link="true" @click="selectTrainer(trainer)" />
    </weui-group>
    <weui-group>
      <template v-if="is_trainer_course || is_coach_course">
        <weui-cell label="锻炼内容" :link="true" :tips="formatProgramsOnly(draft.programs)" @click="selectProgram" />
        <weui-cell v-if="has_program_bulking" label="增肌部位" :link="true" :tips="formatProgramOptions(2, draft.program_options)" @click="selectBulkingOption" />
        <weui-input v-if="has_program_other" label="其他内容" v-model="program_other" />
      </template>
      <weui-input v-if="is_private_course" placeholder="事务描述" v-model="draft.event" />
    </weui-group>
    <weui-group>
      <weui-textarea v-if="is_trainer_course" placeholder="备注，如锻炼前请先热身，或具体动作列表……" v-model="draft.comment" />
      <weui-textarea v-else placeholder="备注" v-model="draft.comment" />
      <weui-switch label="本段备注用户可见" v-model="draft.comment_publicly" />
    </weui-group>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import weui from 'weui.js';

import { WeuiGroup, WeuiCell, WeuiSwitch, WeuiInput, WeuiTextarea } from '@/components/weui';
import { ROUTE_TRAINER_VIEW, ROUTE_COURSE_CREATE_WEEKDAYS, ROUTE_COURSE_CREATE_PROGRAMS, ROUTE_COURSE_CREATE_BULKING_OPTIONS, ROUTE_COURSE_VIEW_WEEKDAYS, ROUTE_COURSE_VIEW_PROGRAMS, ROUTE_COURSE_VIEW_BULKING_OPTIONS } from '@/router/names';
import { COURSE_TYPE_TRAINER, COURSE_TYPE_COACH, COURSE_TYPE_PRIVATE } from '@/constants/types';
import { PROGRAM_BULKING, PROGRAM_OTHER } from '@/constants/programs';
import { FETCH_AVAILABLE_TIMES } from '@/store/coach/types';
import { FETCH_TRAINER } from '@/store/trainer/types';
import mixinFormatters from '@/mixins/formatters';
import mixinUniqid from '@/mixins/uniqid';
import datetimeUtils from '@/utils/datetime';
import { COURSE_DRAFT_FILL } from '@/store/course/types';

const { mapState: mapCoachState, mapActions: mapCoachActions } = createNamespacedHelpers('coach');
const { mapState: mapTrainerState, mapActions: mapTrainerActions } = createNamespacedHelpers('trainer');
const { mapState: mapCourseState, mapMutations: mapCourseMutations } = createNamespacedHelpers('course');

export default {
    props: {
        id: {
            type: [Number, String],
            default: 0,
        },
    },
    data() {
        return {
            undetermined: false,
            start_time: null,
            end_time: null,
        };
    },
    computed: {
        ...mapCoachState({
            available_timespans: state => state.available_timespans,
        }),
        ...mapTrainerState({
            trainer: state => state.trainer,
        }),
        ...mapCourseState({
            draft: state => state.draft,
        }),
        is_trainer_course() {
            return this.draft.type === COURSE_TYPE_TRAINER;
        },
        is_coach_course() {
            return this.draft.type === COURSE_TYPE_COACH;
        },
        is_private_course() {
            return this.draft.type === COURSE_TYPE_PRIVATE;
        },
        has_program_bulking() {
            const { programs } = this.draft;
            return Array.isArray(programs) && programs.indexOf(PROGRAM_BULKING) >= 0;
        },
        has_program_other() {
            const { programs } = this.draft;
            return Array.isArray(programs) && programs.indexOf(PROGRAM_OTHER) >= 0;
        },
        program_other: {
            get() {
                return this.formatProgramOptions(PROGRAM_OTHER, this.draft.program_options);
            },
            set(val) {
                this.draft.program_options[PROGRAM_OTHER] = val;
            },
        },
        available_timespan_options() {
            const timespanOptions = [];
            this.available_timespans.forEach((tss) => {
                const startSeconds = datetimeUtils.fromTimeString(tss.start_time);
                const endSeconds = datetimeUtils.fromTimeString(tss.end_time);
                if (endSeconds - startSeconds >= 3600) {
                    for (let ptrStartSeconds = startSeconds; ptrStartSeconds <= endSeconds - 3600; ptrStartSeconds += 1800) {
                        const ptrStartTimeString = datetimeUtils.toTimeString(ptrStartSeconds);
                        const timespan = {
                            start_time: ptrStartTimeString,
                            end_times: [],
                        };
                        for (let ptrEndSeconds = ptrStartSeconds + 3600; ptrEndSeconds <= endSeconds; ptrEndSeconds += 1800) {
                            const ptrEndTimeString = datetimeUtils.toTimeString(ptrEndSeconds);
                            timespan.end_times.push(ptrEndTimeString);
                        }

                        timespanOptions.push(timespan);
                    }
                }
            });

            return timespanOptions;
        },
    },
    mounted() {
        if (!this.draft.date) {
            this.draft.date = datetimeUtils.getToday(true);
        }
        this.fetchAvailableTimes({
            date: this.draft.date,
        });

        if (this.draft.trainer_id) {
            this.fetchTrainer({
                id: this.draft.trainer_id,
            });
        }

        this.start_time = this.draft.start_time;
        this.end_time = this.draft.end_time;
    },
    watch: {
        undetermined(val) {
            if (val) {
                this.fillDraft({
                    start_time: null,
                    end_time: null,
                });
            } else {
                this.fillDraft({
                    start_time: this.start_time,
                    end_time: this.end_time,
                });
            }
        },
        // eslint-disable-next-line func-names
        'draft.trainer_id': function (val, oldVal) {
            if (val !== oldVal) {
                this.fetchTrainer({
                    id: val,
                    safe: true,
                });
            }
        },
        // eslint-disable-next-line func-names
        'draft.start_time': function (val, oldVal) {
            if (val !== oldVal) {
                this.undetermined = !val;
            }
            if (val !== null) {
                this.start_time = val;
            }
        },
        // eslint-disable-next-line func-names
        'draft.end_time': function (val) {
            if (val !== null) {
                this.end_time = val;
            }
        },
    },
    methods: {
        ...mapCoachActions({
            fetchAvailableTimes: FETCH_AVAILABLE_TIMES,
        }),
        ...mapTrainerActions({
            fetchTrainer: FETCH_TRAINER,
        }),
        ...mapCourseMutations({
            fillDraft: COURSE_DRAFT_FILL,
        }),
        selectDate() {
            const vm = this;
            const undetermined = this.undetermined;
            const today = datetimeUtils.getToday();
            const day = new Date(this.draft.date);
            const confirmDate = (result) => {
                const selected = new Date(result[0].value, result[1].value - 1, result[2].value);
                const newDate = vm.formatISODate(selected);
                if (vm.draft.date !== newDate) {
                    vm.fillDraft({
                        date: newDate,
                        start_time: null,
                        end_time: null,
                    });
                    vm.fetchAvailableTimes({
                        date: newDate,
                    });
                    vm.$nextTick(() => {
                        vm.undetermined = undetermined;
                    });
                }
            };
            weui.datePicker({
                id: this.uniqid(),
                start: today,
                end: new Date(today.getFullYear(), today.getMonth() + 3, today.getDate()),
                defaultValue: [day.getFullYear(), day.getMonth() + 1, day.getDate()],
                onConfirm: confirmDate,
            });
        },
        selectStartTime() {
            const options = this.available_timespan_options.map(ts => ({ label: this.formatTime(ts.start_time), value: ts.start_time, end_times: ts.end_times })) || [];
            if (options.length === 0) {
                return;
            }
            const defaultStartTime = this.draft.start_time || options[0].value;
            weui.picker(options, {
                id: this.uniqid(),
                defaultValue: [defaultStartTime],
                onConfirm: (result) => {
                    if (this.draft.start_time !== result[0].value) {
                        this.fillDraft({
                            start_time: result[0].value,
                            end_time: result[0].end_times[0],
                        });
                    }
                },
            });
        },
        selectEndTime() {
            if (!this.draft.start_time) {
                return;
            }
            const timespan = this.available_timespan_options.find(ts => ts.start_time === this.draft.start_time);
            if (timespan) {
                const options = timespan.end_times.map(value => ({ label: this.formatTime(value), value })) || [];
                if (options.length === 0) {
                    return;
                }
                const defaultEndTime = this.draft.end_time || options[0].value;
                weui.picker(options, {
                    id: this.uniqid(),
                    defaultValue: [defaultEndTime],
                    onConfirm: (result) => {
                        this.fillDraft({
                            end_time: result[0].value,
                        });
                    },
                });
            }
        },
        selectWeekdays() {
            if (this.id) {
                this.$router.push({
                    name: ROUTE_COURSE_VIEW_WEEKDAYS,
                    params: {
                        id: this.id,
                    },
                });
            } else {
                this.$router.push({
                    name: ROUTE_COURSE_CREATE_WEEKDAYS,
                });
            }
        },
        selectTrainer(trainer) {
            this.$router.push({
                name: ROUTE_TRAINER_VIEW,
                params: {
                    id: trainer.id,
                },
            });
        },
        selectProgram() {
            if (this.id) {
                this.$router.push({
                    name: ROUTE_COURSE_VIEW_PROGRAMS,
                    params: {
                        id: this.id,
                    },
                });
            } else {
                this.$router.push({
                    name: ROUTE_COURSE_CREATE_PROGRAMS,
                });
            }
        },
        selectBulkingOption() {
            if (this.id) {
                this.$router.push({
                    name: ROUTE_COURSE_VIEW_BULKING_OPTIONS,
                    params: {
                        id: this.id,
                    },
                });
            } else {
                this.$router.push({
                    name: ROUTE_COURSE_CREATE_BULKING_OPTIONS,
                });
            }
        },
    },
    mixins: [
        mixinFormatters,
        mixinUniqid,
    ],
    components: {
        WeuiGroup,
        WeuiCell,
        WeuiSwitch,
        WeuiInput,
        WeuiTextarea,
    },
};
</script>

<style lang="scss" scoped>
.info-line {
    font-size: 3.466666667vw;
    line-height: 1.4;
    color: #9b9b9b;
}
</style>



// WEBPACK FOOTER //
// src/components/course/Editor.vue